import { formatShareCode, type Plan } from "@roxpacer/core";
import { useEffect, useState, type ReactNode } from "react";
import { RaceCardTable, planDescription } from "../components/RaceCardTable";
import { SplitChart } from "../components/SplitChart";
import { Summary } from "../components/Summary";
import { config } from "../config";
import { navigate, sharePath } from "../lib/router";
import { usePlans } from "../store";
import { fetchSharedPlan } from "../sync/api";
import { RaceCardPage } from "./RaceCardPage";

type Shared =
  | { status: "loading" }
  | { status: "found"; plan: Plan; expiresAt: string }
  | { status: "not-found" }
  | { status: "error" };

function useSharedPlan(code: string): Shared {
  const [result, setResult] = useState<{ code: string; shared: Shared } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSharedPlan(config.apiUrl, code).then(
      (shared) => !cancelled && setResult({ code, shared }),
      () => !cancelled && setResult({ code, shared: { status: "error" } }),
    );
    return () => {
      cancelled = true;
    };
  }, [code]);

  return result?.code === code ? result.shared : { status: "loading" };
}

function Message({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="mx-auto mt-8 max-w-md px-4">
      <div className="card p-6 text-center">
        <h1 className="text-lg font-semibold">{title}</h1>
        <div className="mt-2 text-sm text-ink-2">{children}</div>
        <button type="button" className="btn btn-primary mt-4" onClick={() => navigate("/")}>
          Go to the planner
        </button>
      </div>
    </main>
  );
}

function SharedStates({
  code,
  render,
}: {
  code: string;
  render: (shared: Extract<Shared, { status: "found" }>) => ReactNode;
}) {
  const shared = useSharedPlan(code);
  switch (shared.status) {
    case "loading":
      return (
        <p className="mt-8 text-center text-sm text-muted" role="status">
          Loading shared plan…
        </p>
      );
    case "not-found":
      return (
        <Message title="Share code not found">
          <p>
            <span className="font-medium text-ink">{formatShareCode(code)}</span> doesn't match a
            shared plan. It may have expired or been turned off by its owner.
          </p>
        </Message>
      );
    case "error":
      return (
        <Message title="Couldn't load the plan">
          <p>Check your connection and try again.</p>
        </Message>
      );
    case "found":
      return <>{render(shared)}</>;
  }
}

export function SharedPlanPage({ code }: { code: string }) {
  const importPlan = usePlans((s) => s.importPlan);

  return (
    <SharedStates
      code={code}
      render={({ plan, expiresAt }) => (
        <main className="mx-auto max-w-6xl space-y-4 px-4 py-4">
          <section className="card flex flex-col gap-4 p-4 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <p className="label">Shared plan · {formatShareCode(code)}</p>
              <h1 className="mt-0.5 text-2xl font-semibold tracking-tight break-words">
                {plan.name}
              </h1>
              <p className="text-sm text-ink-2">{planDescription(plan)}</p>
              <p className="mt-1 text-xs text-muted">
                Read-only. Shows the owner's latest changes until{" "}
                {new Date(expiresAt).toLocaleDateString(undefined, { dateStyle: "medium" })}.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn" onClick={() => navigate(sharePath(code, true))}>
                Race card
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  importPlan({ ...structuredClone(plan), id: crypto.randomUUID() });
                  navigate("/");
                }}
              >
                Copy to my plans
              </button>
            </div>
          </section>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
            <section className="card p-4" aria-labelledby="targets-heading">
              <h2 id="targets-heading" className="label mb-1">
                Targets
              </h2>
              <RaceCardTable plan={plan} />
            </section>
            <aside className="space-y-4">
              <Summary plan={plan} />
              <SplitChart plan={plan} />
            </aside>
          </div>
        </main>
      )}
    />
  );
}

export function SharedRaceCardPage({ code }: { code: string }) {
  return (
    <SharedStates
      code={code}
      render={({ plan }) => <RaceCardPage plan={plan} backTo={sharePath(code)} />}
    />
  );
}
